import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import LoginForm from './components/login-form';
import TestimonialList from './components/testimonial';
// import { Footer } from '../../components/layout/Footer';
import { resetAuthSessionFlags } from '@/utils/auth-session';
import { resolveEnterpriseSsoOrganization } from '@/utils/auth0-organization';
import { getErrorMessage } from '@/utils/common';
import { getPostLoginReturnPath } from '@/utils/post-login-return-path';
import {
  clearPostLogoutLoginPath,
  clearSoulMapLoginFlow,
} from '@/utils/soulmap-auth';
import { Link } from '@nextui-org/react';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'usehooks-ts';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useEnterTenant } from '../../hooks/useEnterTenant';
import { useSsoLogin } from '../../hooks/useSsoLogin';
import { useAuthStore } from '../../store/use-auth-store';
import Features from './components/features';
import Navbar from './components/navbar';
import TenantSelector from './components/tenant-selector';

interface Tenant {
  id: number;
  name: string;
  slug: string;
}

const LiftLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const organization = resolveEnterpriseSsoOrganization({
    queryOrg: searchParams.get('org'),
  });
  const { login } = useAuthQuery();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { setShowTenantSelector, showTenantSelector } = useAuthStore();
  const enterTenant = useEnterTenant();
  const { loginWithSso, isLoading: isSsoLoading } = useSsoLogin();
  const [failedLoginAttempt, setFailedLoginAttempt] = useState(0);
  const [lockedEmail, setLockedEmail] = useState<string | null>(null);

  useEffect(() => {
    resetAuthSessionFlags();
    clearSoulMapLoginFlow();
    clearPostLogoutLoginPath();
  }, []);

  useEffect(() => {
    setShowTenantSelector(Boolean(location.state?.showTenantSelector));
  }, [location.state?.showTenantSelector, setShowTenantSelector]);

  const handleLogin = async (email: string, password: string) => {
    const normalizedEmail = email.trim();
    setLockedEmail(normalizedEmail);

    login.mutate(
      { email: normalizedEmail, password },
      {
        onSuccess: (data) => {
          setFailedLoginAttempt(0);
          setLockedEmail(null);
          clearSoulMapLoginFlow();
          const tenants = (data?.tenants || []) as Tenant[];
          // A link opened while signed out (e.g. a practice from an email)
          // already names its tenant, so there is nothing to pick.
          if (tenants.length > 1 && !tenantSlug) {
            const returnTenant = tenants.find((t) =>
              getPostLoginReturnPath([t])
            );
            if (returnTenant) {
              enterTenant(returnTenant);
            } else {
              setShowTenantSelector(true);
            }
          } else {
            const selectedTenant = tenantSlug
              ? tenants.find((t: Tenant) => t.slug === tenantSlug)
              : tenants[0];

            if (selectedTenant) {
              navigate(
                getPostLoginReturnPath([selectedTenant]) ??
                  `/${selectedTenant.slug}/home`,
                { replace: true }
              );
            } else {
              // If tenant not found, show tenant selector
              setShowTenantSelector(true);
            }
          }
        },
        onError: (error) => {
          console.error('Login failed:', error);
          setFailedLoginAttempt((attempt) => attempt + 1);
          const message = isAxiosError(error)
            ? getErrorMessage(error.response?.data)
            : getErrorMessage(error);
          toast.error(message);
        },
      }
    );
  };

  // Plain element (not an inline component) so parent re-renders don't remount
  // the form and drop input focus on mobile.
  const loginSection = (
    <div className="w-full md:w-[35%] bg-[#FAF6F5] p-4 md:p-6 rounded border-[#FAF6F5] border-1 mt-6 md:mt-0">
      <div className="p-4">
        {showTenantSelector ? (
          <TenantSelector />
        ) : (
          <>
            <div className="mb-6 md:mb-8">
              <p className="text-medium text-red-500">Login</p>
            </div>
            <LoginForm
              onSubmit={handleLogin}
              isLoading={login.isPending}
              failedLoginAttempt={failedLoginAttempt}
              lockedEmail={lockedEmail}
              loginWithGoogleSSO={() => {
                clearSoulMapLoginFlow();
                setShowTenantSelector(false);
                loginWithSso({
                  authorizationParams: {
                    prompt: 'login',
                    ...(organization && { organization }),
                  },
                });
              }}
              isLoadingWithSSO={isSsoLoading}
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full p-4 md:p-6">
      <Navbar />

      {/* Main Content */}
      <div className="flex flex-col md:flex-row flex-1 gap-8 md:gap-16 px-4 md:px-12">
        {/* Left Content Section */}
        <div className="w-full md:w-[60%] sm:w-[90%] mt-8 md:mt-6 flex flex-col justify-between">
          <div className="flex flex-col gap-6">
            <p className="text-xl font-normal md:text-4xl md:!leading-[3.5rem] sm:!leading-relaxed">
              Enables managers to deliver
              <span className="font-extrabold ml-1">breakthrough</span>{' '}
              performance
            </p>
            <div>
              <p className="text-xl mb-6">
                Unlock a{' '}
                <span className="text-red-400 font-semibold">
                  scientific approach
                </span>{' '}
                for character and leadership development
              </p>
              {/* Login section on mobile - appears before Features */}
              <Features />
              {isMobile && loginSection}
            </div>
            <TestimonialList />
          </div>
          {/* <Footer /> */}
          {!isMobile && (
            <div className="mt-auto pt-8 flex gap-4 align-end">
              <Link
                href="https://mentora.institute/termsofuse-mentora/"
                target="_blank"
              >
                <span className="text-gray-700 text-sm">
                  Terms & Conditions
                </span>
              </Link>
              {/* <Link href="#">
                <span className="text-gray-700 text-sm">Privacy Policy</span>
              </Link> */}
            </div>
          )}
        </div>

        {/* Right Login Section - Desktop only */}
        {!isMobile && loginSection}
      </div>

      {/* Footer Links */}
      {isMobile && (
        <div className="mt-auto pt-8 flex gap-4 align-center justify-center">
          <Link href="#">
            <span className="text-gray-700 text-sm">Terms & Conditions</span>
          </Link>
          {/* <Link href="#">
            <span className="text-gray-700 text-sm">Privacy Policy</span>
          </Link> */}
        </div>
      )}
    </div>
  );
};

export default LiftLoginPage;
